import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Ruler, Scissors, Users, Receipt, BarChart3,
  Package, Factory, Briefcase, Truck, Trash2, Banknote, Settings, CircleDollarSign, Settings2,
  Wallet, ClipboardList, ClipboardCheck, UserRound, SlidersHorizontal, X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { fetchMyAccess } from '../../api/resources';
import { useAuth } from '../../context/AuthContext';
import { LogoMark } from '../ui/Logo';
import { moduleColor } from '../../utils/modules';
import { hasPortalGroup } from '../../utils/roles';

// Hide the not-yet-built ("Soon") menu items for now. Flip this to false to
// show them again — the entries themselves are left untouched below.
const HIDE_UPCOMING = true;

// One flat list, in the order the shop asked for.
//
// This used to be grouped into collapsible sections. Six of those sections
// held a single link each, so reaching one link meant opening a heading first
// — which is what made the menu feel heavy. The accordion is gone, and the
// order and wording below come from the shop rather than from the modules.
//
// `end: true` = exact-path match, so "My Dashboard" is not also highlighted
// on /portal/orders.
const NAV = [
  // --- worker portal (WORKER logins see only these) ----------------------
  { to: '/portal', label: 'My Dashboard', icon: LayoutDashboard, roles: ['WORKER'], status: 'working', end: true, workerOnly: true },
  { to: '/portal/orders', label: 'My Orders', icon: ClipboardList, roles: ['WORKER'], status: 'working', workerOnly: true },
  { to: '/portal/payouts', label: 'My Payouts', icon: Wallet, roles: ['WORKER'], status: 'working', workerOnly: true },

  // --- staff and admin, in the order the shop asked for -------------------
  // The read-only "Order / Invoice Viewer" (/view/invoices) is deliberately
  // not listed: it showed the same orders as the entry screen, which read as
  // two menus for one job. The route still works if linked directly.
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['ADMIN', 'SUPERVISOR', 'STAFF'], status: 'working' },
  { to: '/tailoring-items', label: 'Items', icon: Scissors, roles: ['ADMIN', 'STAFF'], status: 'working' },
  { to: '/customers', label: 'Customers', icon: Users, roles: ['ADMIN', 'SUPERVISOR', 'STAFF'], status: 'working' },
  // The two order-desk logins see this and nothing else.
  { to: '/invoices', label: 'Orders', icon: Receipt, roles: ['ADMIN', 'SUPERVISOR', 'STAFF', 'STORE_MANAGER', 'WORKSHOP_MANAGER'], status: 'working' },
  { to: '/workers', label: 'Workers', icon: Briefcase, roles: ['ADMIN', 'SUPERVISOR'], status: 'working' },
  { to: '/finance/settlements', label: 'Worker Payments', icon: Wallet, roles: ['ADMIN'], status: 'working' },
  { to: '/utility/worker-item-rates', label: 'Worker & Item Rates', icon: Settings2, roles: ['ADMIN'], status: 'working' },
  { to: '/measurements', label: 'Measurements', icon: Ruler, roles: ['ADMIN'], status: 'working' },
  { to: '/reports', label: 'Reports', icon: BarChart3, roles: ['ADMIN', 'STAFF', 'VIEWER'], status: 'working' },
  { to: '/salesmen', label: 'Salesmen', icon: UserRound, roles: ['ADMIN', 'SUPERVISOR', 'STAFF'], status: 'working' },
  { to: '/portal-access', label: 'Portal Access', icon: SlidersHorizontal, roles: ['ADMIN'], status: 'working' },

  // --- not built yet (hidden while HIDE_UPCOMING is true) -----------------
  { to: '/fabrics', label: 'Fabrics / Readymades', icon: Package, roles: ['ADMIN', 'STAFF'], status: 'dummy' },
  { to: '/process-types', label: 'Process / Work Type', icon: Factory, roles: ['ADMIN'], status: 'dummy' },
  { to: '/vendors', label: 'Vendor / Supplier', icon: Truck, roles: ['ADMIN'], status: 'dummy' },
  { to: '/expenses', label: 'Expense', icon: Trash2, roles: ['ADMIN'], status: 'dummy' },
  { to: '/miscellaneous', label: 'Miscellaneous', icon: Settings, roles: ['ADMIN'], status: 'dummy' },
  { to: '/cash-bank', label: 'Cash / Bank', icon: Banknote, roles: ['ADMIN'], status: 'dummy' },
  { to: '/ledger', label: 'A/c Ledger', icon: CircleDollarSign, roles: ['ADMIN'], status: 'dummy' },
  { to: '/business-profile', label: 'Business Profile', icon: Settings, roles: ['ADMIN'], status: 'dummy' },
];

function Tooltip({ label }) {
  return (
    <span className="pointer-events-none absolute left-full z-50 ml-2 whitespace-nowrap rounded-md bg-content px-2 py-1 text-2xs font-medium text-surface opacity-0 shadow-pop transition-opacity duration-150 group-hover:opacity-100">
      {label}
    </span>
  );
}

function NavItem({ item, collapsed, onNavigate, active }) {
  const Icon = item.icon;
  const rgb = moduleColor(item.to);
  const isDummy = item.status === 'dummy';
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      style={active && rgb ? { backgroundColor: `rgb(${rgb} / 0.12)` } : undefined}
      title={collapsed ? item.label : undefined}
      className={`group relative flex items-center rounded-md text-sm transition-colors
        ${collapsed ? 'justify-center px-0 py-2' : 'gap-2.5 px-2.5 py-2.5 lg:py-[7px]'}
        ${active
          ? 'font-semibold text-content'
          : 'font-medium text-content-muted hover:bg-surface-hover hover:text-content'}
        ${isDummy && !active ? ' text-content-subtle' : ''}`}
    >
      {/* Active marker sits flush with the rail edge */}
      {active && (
        <span className="absolute -left-3 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r" style={{ backgroundColor: rgb ? `rgb(${rgb})` : undefined }} />
      )}
      <Icon
        size={17}
        strokeWidth={active ? 2.2 : 1.9}
        // Each menu keeps its own colour, so it can be found by colour alone.
        className="shrink-0"
        style={rgb ? { color: `rgb(${rgb})`, opacity: active ? 1 : 0.8 } : undefined}
      />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && isDummy && (
        <span className="ml-auto rounded bg-surface-sunken px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-content-subtle">
          Soon
        </span>
      )}
      {collapsed && <Tooltip label={item.label} />}
    </NavLink>
  );
}

// What a portal login is allowed to open, beyond their own jobs. The admin
// sets this on Portal Access; the server enforces it, and this only decides
// whether the door is worth showing.
const PORTAL_EXTRAS = [
  { supervisor: true, to: '/portal/supervisor', label: 'Supervise', icon: ClipboardCheck },
  { need: 'orders', to: '/invoices', label: 'Orders', icon: Receipt },
  { need: 'customers', to: '/customers', label: 'Customers', icon: Users },
  { need: 'production', to: '/production', label: 'Production', icon: Factory },
  { need: 'workers', to: '/workers', label: 'Workers', icon: Briefcase },
];

export default function Sidebar({ collapsed, onNavigate, onClose }) {
  const { role, user, companyId } = useAuth();
  const location = useLocation();
  const isActive = (item) => (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to));

  // Only a login held to an account group needs to ask — workers, supervisors
  // and the two manager desks. Everyone else has a role-based menu.
  const grouped = hasPortalGroup(role);
  const { data: me } = useQuery({
    queryKey: ['my-access', companyId ?? null, user?.id ?? null],
    queryFn: () => fetchMyAccess(companyId),
    enabled: grouped && !!companyId,
    staleTime: 60_000,
  });
  const access = me?.access || {};

  const isSupervisor = !!me?.isSupervisor;

  // A manager's role puts Orders on their menu, but a switch still governs it:
  // with Orders off the link has to go, or it offers a door the guard shuts.
  const needFor = (to) => PORTAL_EXTRAS.find((e) => e.to === to && e.need)?.need;

  const byRole = NAV.filter((i) => i.roles.includes(role)
    && !(HIDE_UPCOMING && i.status === 'dummy')
    // A supervisor runs the floor rather than sewing on it, so the screens
    // about their own jobs are not theirs.
    && !(i.workerOnly && isSupervisor)
    && !(grouped && needFor(i.to) && !access[needFor(i.to)]));

  // An extra switched on for a screen the role already lists must not appear twice.
  const extras = grouped
    ? PORTAL_EXTRAS.filter((e) => (e.supervisor ? isSupervisor : access[e.need])
        && !byRole.some((i) => i.to === e.to))
    : [];

  const items = [...byRole, ...extras];

  return (
    <div className="flex h-full flex-col overflow-hidden border-r border-line bg-surface">
      {/* Drawer-only header. The desktop rail has the topbar above it, but the
          drawer floats over the page and needs its own way out. */}
      {onClose && (
        <div className="flex shrink-0 items-center justify-between border-b border-line px-3 py-2.5">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-content">
            <LogoMark size={16} />
            Atelier ERP
          </span>
          <button onClick={onClose} className="btn-icon" aria-label="Close menu">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="sidebar-scroll flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-3 py-3">
        {items.map((item) => (
          <NavItem key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} active={isActive(item)} />
        ))}
      </nav>

      {/* Footer */}
      <div className="shrink-0 border-t border-line px-3 py-2.5">
        {collapsed ? (
          <p className="text-center text-2xs font-semibold text-content-subtle">v1.0</p>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-2xs text-content-subtle">
              <LogoMark size={14} />
              <span className="font-medium">Atelier ERP</span>
            </span>
            <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] font-semibold text-content-subtle">
              v1.0
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
